/**
 * User Service
 *
 * This service provides utility functions for user management.
 * In production, these would be API calls to the backend.
 */

/**
 * Send welcome email to new user
 * @param {Object} user - User object
 * @returns {Promise<boolean>} - Success status
 */
export const sendWelcomeEmail = async (user) => {
  // In production, this would be an API call to send email
  console.log(`📧 Sending welcome email to ${user.email}`);
  console.log(`Email content:
    - Welcome to AviPair, ${user.name}!
    - Your account has been created
    - Role: ${user.role}
    - Access: ${user.visibleMenus?.join(", ")}
  `);

  // Simulate API delay
  return new Promise((resolve) => {
    setTimeout(() => {
      console.log(`✅ Welcome email sent to ${user.email}`);
      resolve(true);
    }, 500);
  });
};

/**
 * Send role change approval email
 * @param {Object} user - User object
 * @param {string} newRole - New role
 * @returns {Promise<boolean>} - Success status
 */
export const sendRoleChangeApprovalEmail = async (user, newRole) => {
  console.log(`📧 Sending role change approval email to ${user.email}`);
  console.log(`Email content:
    - Your role change request has been approved
    - New role: ${newRole}
    - You now have access to new features
  `);

  return new Promise((resolve) => {
    setTimeout(() => {
      console.log(`✅ Role change approval email sent to ${user.email}`);
      resolve(true);
    }, 500);
  });
};

/**
 * Send pilot app download notification
 * @param {Object} user - User object
 * @returns {Promise<boolean>} - Success status
 */
export const sendPilotAppDownloadEmail = async (user) => {
  console.log(`📧 Sending pilot app download notification to ${user.email}`);
  console.log(`Email content:
    - Welcome to AviPair Pilot Program!
    - Download the mobile app to get started
    - iOS: [App Store Link]
    - Android: [Play Store Link]
  `);

  return new Promise((resolve) => {
    setTimeout(() => {
      console.log(`✅ Pilot app download email sent to ${user.email}`);
      resolve(true);
    }, 500);
  });
};

/**
 * Send role change rejection email
 * @param {Object} user - User object
 * @param {string} requestedRole - Requested role
 * @returns {Promise<boolean>} - Success status
 */
export const sendRoleChangeRejectionEmail = async (user, requestedRole) => {
  console.log(`📧 Sending role change rejection email to ${user.email}`);
  console.log(`Email content:
    - Your request to change role to ${requestedRole} has been declined
    - Please contact support for more information
  `);

  return new Promise((resolve) => {
    setTimeout(() => {
      console.log(`✅ Role change rejection email sent to ${user.email}`);
      resolve(true);
    }, 500);
  });
};

/**
 * Validate user data
 * @param {Object} userData - User data to validate
 * @returns {Object} - Validation result {valid: boolean, errors: Object}
 */
export const validateUserData = (userData) => {
  const errors = {};

  if (!userData.name?.trim()) {
    errors.name = "Name is required";
  }

  if (!userData.email?.trim()) {
    errors.email = "Email is required";
  } else if (!/\S+@\S+\.\S+/.test(userData.email)) {
    errors.email = "Email is invalid";
  }

  if (userData.role === "user" && !userData.password?.trim()) {
    errors.password = "Password is required for general users";
  } else if (userData.role === "user" && userData.password?.length < 6) {
    errors.password = "Password must be at least 6 characters";
  }

  if (!userData.city?.trim()) {
    errors.city = "City is required";
  }

  if (!userData.state) {
    errors.state = "State is required";
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  };
};

/**
 * Format user data for display
 * @param {Object} user - User object
 * @returns {Object} - Formatted user data
 */
export const formatUserForDisplay = (user) => {
  return {
    ...user,
    fullLocation: `${user.city}, ${user.state}`,
    roleLabel: user.role.charAt(0).toUpperCase() + user.role.slice(1),
    statusLabel: user.status.charAt(0).toUpperCase() + user.status.slice(1),
  };
};

const userService = {
  sendWelcomeEmail,
  sendRoleChangeApprovalEmail,
  sendPilotAppDownloadEmail,
  sendRoleChangeRejectionEmail,
  validateUserData,
  formatUserForDisplay,
};

export default userService;
